// src/quiz/mba-mock7/Mba7Test.js
// Section-wise test page: every question of the current section is listed on one page,
// with a full-height "Question Palette" panel on the right.
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { mba7Sections, mba7AllQuestions, mba7Contexts, mba7Meta } from "./mba7data";
import { savePgcetMbaResult } from "../pgcetMbaMocks";

/* false = palette shows only the current section (switches when you move to the next section)
   true  = palette shows all 5 sections at once */
const SHOW_ALL_SECTIONS_IN_PALETTE = false;

const STORAGE_KEY = "mba7_test_state_v1";
const LETTERS = ["A", "B", "C", "D", "E"];

const fmtTime = (s) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

const ClockIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);
const FlagIcon = ({ filled }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 21V4" />
    <path d="M5 4h12l-2 4 2 4H5" />
  </svg>
);

/* Renders context text; consecutive lines containing "|" become a table */
function ContextText({ text }) {
  const blocks = [];
  let tableRows = [];
  const flush = () => {
    if (tableRows.length) {
      blocks.push({ type: "table", rows: tableRows });
      tableRows = [];
    }
  };
  text.split("\n").forEach((line) => {
    if (line.includes("|")) {
      tableRows.push(line.split("|").map((c) => c.trim()));
    } else {
      flush();
      if (line.trim()) blocks.push({ type: "p", text: line });
    }
  });
  flush();

  return (
    <>
      {blocks.map((b, i) =>
        b.type === "p" ? (
          <p key={i} className="mk7-p">{b.text}</p>
        ) : (
          <div key={i} className="mk7-table-wrap">
            <table className="mk7-table">
              <tbody>
                {b.rows.map((r, ri) => (
                  <tr key={ri}>
                    {r.map((c, ci) => (ri === 0 ? <th key={ci}>{c}</th> : <td key={ci}>{c}</td>))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </>
  );
}

/* Groups consecutive questions that share the same passage / data block */
const groupQuestions = (questions) => {
  const groups = [];
  questions.forEach((x) => {
    const last = groups[groups.length - 1];
    if (x.contextId && last && last.contextId === x.contextId) last.items.push(x);
    else groups.push({ contextId: x.contextId || null, items: [x] });
  });
  return groups;
};

export default function Mba7Test() {
  const navigate = useNavigate();
  const total = mba7AllQuestions.length;
  const nSections = mba7Sections.length;

  const [sectionIdx, setSectionIdx] = useState(0);
  const [activeId, setActiveId] = useState(1);
  const [answers, setAnswers] = useState({}); // {questionId: optionIndex}
  const [marked, setMarked] = useState({}); // {questionId: true}
  const [visited, setVisited] = useState({ 1: true }); // {questionId: true}
  const [secondsLeft, setSecondsLeft] = useState(mba7Meta.durationMinutes * 60);
  const [submitted, setSubmitted] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const [scrollReq, setScrollReq] = useState(null);
  const endTimeRef = useRef(null);
  const scrollRef = useRef(null);

  // full-screen test: hide the site header/footer and stop the page behind from scrolling
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  /* ---------- restore after refresh ---------- */
  useEffect(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null");
      if (saved && saved.endTime && !saved.submitted) {
        endTimeRef.current = saved.endTime;
        setAnswers(saved.answers || {});
        setMarked(saved.marked || {});
        setVisited(saved.visited || { 1: true });
        setSectionIdx(saved.sectionIdx || 0);
        setActiveId(saved.activeId || 1);
        setSecondsLeft(Math.max(0, Math.round((saved.endTime - Date.now()) / 1000)));
        return;
      }
    } catch (e) {
      /* ignore */
    }
    endTimeRef.current = Date.now() + mba7Meta.durationMinutes * 60 * 1000;
  }, []);

  /* ---------- autosave ---------- */
  useEffect(() => {
    if (!endTimeRef.current) return;
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ endTime: endTimeRef.current, answers, marked, visited, sectionIdx, activeId, submitted })
      );
    } catch (e) {
      /* ignore */
    }
  }, [answers, marked, visited, sectionIdx, activeId, submitted]);

  const doSubmit = useCallback(() => {
    setSubmitted(true);
    setConfirmOpen(false);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* ignore */
    }
  }, []);

  /* ---------- timer (end-time based, so it stays correct if the tab sleeps) ---------- */
  useEffect(() => {
    if (submitted) return undefined;
    const t = setInterval(() => {
      if (!endTimeRef.current) return;
      const left = Math.max(0, Math.round((endTimeRef.current - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left <= 0) doSubmit();
    }, 1000);
    return () => clearInterval(t);
  }, [submitted, doSubmit]);

  const section = mba7Sections[sectionIdx];
  const groups = useMemo(() => groupQuestions(section.questions), [section]);

  /* ---------- navigation ---------- */
  const markVisited = (id) => setVisited((v) => (v[id] ? v : { ...v, [id]: true }));

  const goToQuestion = (id) => {
    const si = mba7Sections.findIndex((s) => s.id === mba7AllQuestions[id - 1].sectionId);
    setSectionIdx(si);
    setActiveId(id);
    markVisited(id);
    setScrollReq({ target: id, n: Date.now() });
  };

  const changeSection = (si) => {
    if (si < 0 || si >= nSections) return;
    const first = mba7Sections[si].questions[0].id;
    setSectionIdx(si);
    setActiveId(first);
    markVisited(first);
    setScrollReq({ target: "top", n: Date.now() });
  };

  // scroll after the new section has rendered
  useEffect(() => {
    if (!scrollReq) return;
    if (scrollReq.target === "top") {
      if (scrollRef.current) scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
      const pg = document.querySelector(".mk7-page");
      if (pg && pg.scrollTo) pg.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      const el = document.getElementById(`mk7-q-${scrollReq.target}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [scrollReq]);

  // a question counts as "visited" once it has been on screen
  useEffect(() => {
    if (submitted || reviewMode || typeof IntersectionObserver === "undefined") return undefined;
    const els = document.querySelectorAll("[data-qid]");
    const obs = new IntersectionObserver(
      (entries) => {
        const ids = entries.filter((e) => e.isIntersecting).map((e) => Number(e.target.getAttribute("data-qid")));
        if (ids.length) {
          setVisited((v) => {
            const next = { ...v };
            let changed = false;
            ids.forEach((id) => {
              if (!next[id]) {
                next[id] = true;
                changed = true;
              }
            });
            return changed ? next : v;
          });
        }
      },
      { threshold: 0.35 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [sectionIdx, submitted, reviewMode]);

  /* ---------- answering ---------- */
  const selectOption = (id, idx) => {
    if (reviewMode) return;
    setAnswers((a) => ({ ...a, [id]: idx }));
    setActiveId(id);
    markVisited(id);
  };
  const clearAnswer = (id) =>
    setAnswers((a) => {
      const n = { ...a };
      delete n[id];
      return n;
    });
  const toggleMark = (id) => setMarked((m) => ({ ...m, [id]: !m[id] }));

  /* ---------- counts ---------- */
  const answeredCount = Object.keys(answers).length;
  const markedCount = Object.values(marked).filter(Boolean).length;
  const notAnsweredCount = mba7AllQuestions.filter((x) => visited[x.id] && answers[x.id] === undefined).length;
  const notVisitedCount = total - answeredCount - notAnsweredCount;
  const sectionAnswered = section.questions.filter((x) => answers[x.id] !== undefined).length;

  /* ---------- scoring ---------- */
  const result = useMemo(() => {
    if (!submitted) return null;
    const neg = mba7Meta.negativeMarkPerWrong || 0;
    let score = 0, correct = 0, wrong = 0, skipped = 0;
    const bySection = mba7Sections.map((s) => {
      let sScore = 0, sC = 0, sW = 0, sS = 0;
      s.questions.forEach((x) => {
        const a = answers[x.id];
        if (a === undefined) sS += 1;
        else if (a === x.correct) { sC += 1; sScore += x.marks; }
        else { sW += 1; sScore -= neg * x.marks; }
      });
      score += sScore; correct += sC; wrong += sW; skipped += sS;
      return {
        id: s.id, name: s.name, score: sScore, correct: sC, wrong: sW, skipped: sS,
        max: s.questions.reduce((a, b) => a + b.marks, 0),
      };
    });
    return { score, correct, wrong, skipped, bySection };
  }, [submitted, answers]);

  /* ---------- save the attempt so it shows in the profile (once) ---------- */
  const savedRef = useRef(false);
  useEffect(() => {
    if (!result || savedRef.current) return;
    savedRef.current = true;
    savePgcetMbaResult(7, mba7AllQuestions, answers, result.score, mba7Meta.totalMarks);
  }, [result, answers]);

  /* ---------- palette button class ---------- */
  const paletteClass = (x) => {
    const a = answers[x.id];
    if (reviewMode) {
      const base = a === undefined ? "" : a === x.correct ? "answered" : "wrongp";
      return `${base} ${x.id === activeId ? "ring" : ""}`;
    }
    if (x.id === activeId) return "current";
    if (marked[x.id]) return a !== undefined ? "marked-ans" : "marked";
    if (a !== undefined) return "answered";
    if (visited[x.id]) return "na";
    return "";
  };

  /* =============================== RESULT SCREEN =============================== */
  if (submitted && !reviewMode) {
    return (
      <div className="mk7-page mk7-result-page">
        <style>{CSS}</style>
        <div className="mk7-result">
          <h2 className="mk7-title">{mba7Meta.title} - Result</h2>
          <div className="mk7-score">
            {Number(result.score.toFixed(2))} <span>/ {mba7Meta.totalMarks}</span>
          </div>
          <div className="mk7-stats">
            <div><b>{result.correct}</b>Correct</div>
            <div><b>{result.wrong}</b>Wrong</div>
            <div><b>{result.skipped}</b>Skipped</div>
          </div>
          <div className="mk7-table-wrap">
            <table className="mk7-table" style={{ marginTop: 20 }}>
              <thead>
                <tr><th>Section</th><th>Score</th><th>Correct</th><th>Wrong</th><th>Skipped</th></tr>
              </thead>
              <tbody>
                {result.bySection.map((s) => (
                  <tr key={s.id}>
                    <td>{s.name}</td>
                    <td>{Number(s.score.toFixed(2))} / {s.max}</td>
                    <td>{s.correct}</td>
                    <td>{s.wrong}</td>
                    <td>{s.skipped}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mk7-actions">
            <button className="mk7-btn primary" onClick={() => { setReviewMode(true); changeSection(0); }}>
              Review answers
            </button>
            <button className="mk7-btn" onClick={() => navigate("/quiz/mba-mock7")}>
              Back to instructions
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* =============================== QUESTION CARD =============================== */
  const renderQuestion = (x) => {
    const userAns = answers[x.id];
    const isActive = x.id === activeId;
    const state = reviewMode ? (userAns === undefined ? "skipped" : userAns === x.correct ? "correct" : "wrong") : "";
    return (
      <div
        key={x.id}
        id={`mk7-q-${x.id}`}
        data-qid={x.id}
        className={`mk7-qcard ${isActive && !reviewMode ? "active" : ""} ${state}`}
        onClick={() => !reviewMode && setActiveId(x.id)}
      >
        <div className="mk7-qhead">
          <span className="mk7-badge">{x.id}</span>
          <div className="mk7-qtext">
            {x.question.split("\n").map((l, i) => (l.trim() ? <div key={i}>{l}</div> : <div key={i} className="mk7-gap" />))}
          </div>
          {!reviewMode && (
            <button
              className={`mk7-flag ${marked[x.id] ? "on" : ""}`}
              title={marked[x.id] ? "Unmark review" : "Mark for review"}
              aria-label="Mark for review"
              onClick={(e) => { e.stopPropagation(); toggleMark(x.id); }}
            >
              <FlagIcon filled={!!marked[x.id]} />
            </button>
          )}
        </div>

        <div className="mk7-opts" role="radiogroup">
          {x.options.map((opt, i) => {
            let cls = "mk7-opt";
            if (reviewMode) {
              if (i === x.correct) cls += " correct";
              else if (i === userAns) cls += " wrong";
            } else if (userAns === i) cls += " selected";
            return (
              <button
                key={i}
                role="radio"
                aria-checked={userAns === i}
                className={cls}
                disabled={reviewMode}
                onClick={(e) => { e.stopPropagation(); selectOption(x.id, i); }}
              >
                {LETTERS[i]}. {opt}
              </button>
            );
          })}
        </div>

        {!reviewMode && userAns !== undefined && (
          <button className="mk7-clear" onClick={(e) => { e.stopPropagation(); clearAnswer(x.id); }}>
            Clear response
          </button>
        )}

        {reviewMode && (
          <div className="mk7-expl">
            <b>
              {userAns === undefined ? "Not attempted" : userAns === x.correct ? "Correct" : "Incorrect"}
              {" - "}Answer: {LETTERS[x.correct]}
            </b>
            <div>{x.explanation}</div>
          </div>
        )}
      </div>
    );
  };

  /* =============================== MAIN VIEW =============================== */
  const paletteSections = SHOW_ALL_SECTIONS_IN_PALETTE
    ? mba7Sections.map((s, i) => ({ s, i }))
    : [{ s: section, i: sectionIdx }];
  const lastSection = sectionIdx === nSections - 1;

  return (
    <div className="mk7-page">
      <style>{CSS}</style>

      <div className="mk7-shell">
        {/* ---------------- left: questions ---------------- */}
        <div className="mk7-main"><div className="mk7-headbar"><div className="mk7-headin">
          <div className="mk7-head">
            <div>
              <h1 className="mk7-title">{mba7Meta.title}{reviewMode ? " - Review" : ""}</h1>
              <div className="mk7-sub">Section {sectionIdx + 1} of {nSections}: {section.name}</div>
            </div>
            {reviewMode ? (
              <button className="mk7-btn" onClick={() => setReviewMode(false)}>Back to result</button>
            ) : (
              <div className={`mk7-timer ${secondsLeft < 600 ? "low" : ""}`} role="timer">
                <ClockIcon />
                <span>{fmtTime(secondsLeft)}</span>
              </div>
            )}
          </div>
          </div></div>
          <div className="mk7-scroll" ref={scrollRef}><div className="mk7-inner">

          {groups.map((g, gi) =>
            g.contextId ? (
              <div className="mk7-split" key={`g${gi}`}>
                <aside className="mk7-passage">
                  <h4>{mba7Contexts[g.contextId].title}</h4>
                  <ContextText text={mba7Contexts[g.contextId].text} />
                </aside>
                <div className="mk7-qs">{g.items.map(renderQuestion)}</div>
              </div>
            ) : (
              <div className="mk7-qs" key={`g${gi}`}>{g.items.map(renderQuestion)}</div>
            )
          )}

          <div className="mk7-bottom">
            <span className="mk7-progress-note">
              {reviewMode ? `Section ${sectionIdx + 1} review` : `${sectionAnswered} of ${section.questions.length} answered in this section`}
            </span>
            <div className="mk7-bottom-btns">
              <button className="mk7-btn" onClick={() => changeSection(sectionIdx - 1)} disabled={sectionIdx === 0}>
                Previous section
              </button>
              {!lastSection ? (
                <button className="mk7-btn primary" onClick={() => changeSection(sectionIdx + 1)}>
                  Next section
                </button>
              ) : reviewMode ? (
                <button className="mk7-btn primary" onClick={() => setReviewMode(false)}>Back to result</button>
              ) : (
                <button className="mk7-btn primary" onClick={() => setConfirmOpen(true)}>Submit test</button>
              )}
            </div>
          </div>
        </div></div></div>

        {/* ---------------- right: question palette ---------------- */}
        <aside className="mk7-side" aria-label="Question palette">
          <div className="mk7-side-head">Question Palette</div>

          <div className="mk7-legend">
            {reviewMode ? (
              <>
                <span><i className="mk7-sq answered" /> Correct</span>
                <span><i className="mk7-sq wrongp" /> Wrong</span>
                <span><i className="mk7-sq" /> Not attempted</span>
              </>
            ) : (
              <>
                <span><i className="mk7-sq answered" /> Answered ({answeredCount})</span>
                <span><i className="mk7-sq na" /> Not answered ({notAnsweredCount})</span>
                <span><i className="mk7-sq" /> Not visited ({notVisitedCount})</span>
                <span><i className="mk7-sq current" /> Current</span>
                <span><i className="mk7-sq marked" /> Marked ({markedCount})</span>
              </>
            )}
          </div>

          <div className="mk7-side-scroll">
            {paletteSections.map(({ s, i }) => (
              <div key={s.id} className="mk7-psec">
                <div className="mk7-ptitle">{i + 1}. {s.name}</div>
                <div className="mk7-pgrid">
                  {s.questions.map((x) => (
                    <button key={x.id} className={`mk7-pbtn ${paletteClass(x)}`} onClick={() => goToQuestion(x.id)}>
                      {x.id}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {!reviewMode && (
            <div className="mk7-side-foot">
              <button className="mk7-btn primary full" onClick={() => setConfirmOpen(true)}>Submit test</button>
            </div>
          )}
        </aside>
      </div>

      {confirmOpen && (
        <div className="mk7-modal-bg">
          <div className="mk7-modal" role="dialog" aria-modal="true">
            <h3>Submit test?</h3>
            <p>
              Answered: <b>{answeredCount}</b> / {total}<br />
              Not answered: <b>{total - answeredCount}</b><br />
              Marked for review: <b>{markedCount}</b>
            </p>
            <div className="mk7-actions">
              <button className="mk7-btn" onClick={() => setConfirmOpen(false)}>Continue test</button>
              <button className="mk7-btn primary" onClick={doSubmit}>Submit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const CSS = `
.mk7-page{--green:#16a34a;--green-dark:#166534;--green-soft:#f0fdf4;--green-line:#d5f0e0;--ink:#1f2937;--muted:#6b7280;--line:#e5e7eb;
  font-family:Inter,system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;color:var(--ink);background:#fff;
  position:fixed;inset:0;z-index:9999;display:flex;flex-direction:column;overflow:hidden}
.mk7-page *{box-sizing:border-box}
.mk7-page button{font-family:inherit;cursor:pointer}
.mk7-page button:focus-visible{outline:3px solid rgba(22,163,74,.4);outline-offset:2px}

.mk7-shell{display:flex;flex:1;min-height:0}
.mk7-main{flex:1;min-width:0;display:flex;flex-direction:column;min-height:0}
.mk7-headbar{border-bottom:1px solid #d1fae5;background:#fff}
.mk7-headin{max-width:1060px;margin:0 auto;padding:12px 24px}
.mk7-scroll{flex:1;overflow-y:auto;min-height:0}
.mk7-inner{max-width:1060px;margin:0 auto;padding:18px 24px 50px}

/* header */
.mk7-head{display:flex;justify-content:space-between;align-items:center;gap:16px;flex-wrap:wrap}
.mk7-title{margin:0;font-size:1.5rem;font-weight:800;color:var(--green-dark);letter-spacing:-.2px}
.mk7-sub{color:var(--muted);font-size:.9rem;margin-top:2px}
.mk7-timer{display:flex;align-items:center;gap:10px;background:#ecfdf5;border:1px solid #bbf7d0;color:var(--green-dark);
  padding:6px 14px;border-radius:12px;font-size:1.1rem;font-weight:700;font-variant-numeric:tabular-nums;letter-spacing:1px}
.mk7-timer.low{background:#fef2f2;border-color:#fecaca;color:#b91c1c}
.mk7-rule{border-top:1px solid #d1fae5;margin:14px 0 20px}

/* question cards */
.mk7-qs{display:flex;flex-direction:column;gap:14px;min-width:0}
.mk7-qcard{background:#fff;border:1px solid var(--green-line);border-radius:12px;padding:16px 20px 16px;scroll-margin-top:16px;transition:border-color .15s,background .15s}
.mk7-qcard.active{border:1.5px solid var(--green);background:var(--green-soft)}
.mk7-qcard.correct{border-left:5px solid var(--green)}
.mk7-qcard.wrong{border-left:5px solid #dc2626}
.mk7-qcard.skipped{border-left:5px solid #9ca3af}
.mk7-qhead{display:flex;gap:12px;align-items:flex-start}
.mk7-badge{flex:0 0 30px;height:30px;border-radius:8px;background:#dcfce7;color:var(--green-dark);font-weight:700;font-size:.85rem;
  display:flex;align-items:center;justify-content:center}
.mk7-qtext{flex:1;font-size:.96rem;line-height:1.55;padding-top:3px;min-width:0}
.mk7-gap{height:8px}
.mk7-flag{flex:0 0 auto;border:1px solid var(--line);background:#fff;color:var(--muted);border-radius:7px;width:30px;height:30px;
  display:flex;align-items:center;justify-content:center}
.mk7-flag:hover{border-color:#8b5cf6;color:#7c3aed}
.mk7-flag.on{background:#ede9fe;border-color:#8b5cf6;color:#6d28d9}
.mk7-opts{display:flex;flex-direction:column;gap:8px;margin:12px 0 0 42px}
.mk7-opt{width:100%;text-align:left;background:#fff;border:1px solid #d1d5db;border-radius:8px;padding:9px 14px;font-size:.92rem;color:var(--ink);
  transition:border-color .12s,background .12s}
.mk7-opt:hover:not(:disabled){border-color:var(--green)}
.mk7-opt.selected{border:1.5px solid var(--green);background:#dcfce7;color:var(--green-dark);font-weight:600}
.mk7-opt.correct{border:1.5px solid #059669;background:#d1fae5}
.mk7-opt.wrong{border:1.5px solid #dc2626;background:#fee2e2}
.mk7-opt:disabled{cursor:default}
.mk7-clear{margin:8px 0 0 42px;background:none;border:0;color:var(--muted);font-size:.85rem;text-decoration:underline;padding:0}
.mk7-clear:hover{color:#b91c1c}
.mk7-expl{margin:12px 0 0 42px;padding:10px 12px;background:#f0fdf4;border-radius:8px;font-size:.86rem;line-height:1.55}

/* passage + questions */
.mk7-split{display:grid;grid-template-columns:minmax(0,5fr) minmax(0,6fr);gap:16px;align-items:start;margin-bottom:16px}
.mk7-passage{position:sticky;top:0;max-height:calc(100vh - 150px);overflow:auto;background:#f8fafc;border:1px solid #dbe4ee;
  border-left:4px solid #3b82f6;border-radius:12px;padding:14px 16px}
.mk7-passage h4{margin:0 0 8px;color:#1d4ed8;font-size:.92rem}
.mk7-p{margin:0 0 9px;line-height:1.65;font-size:.86rem}
.mk7-split + .mk7-qs,.mk7-qs + .mk7-split,.mk7-qs + .mk7-qs{margin-top:16px}
.mk7-split .mk7-opts,.mk7-split .mk7-clear,.mk7-split .mk7-expl{margin-left:0}
.mk7-split .mk7-qcard{padding:16px}

/* bottom bar */
.mk7-bottom{display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap;margin-top:26px;padding-top:16px;border-top:1px solid var(--line)}
.mk7-progress-note{color:var(--muted);font-size:.82rem}
.mk7-bottom-btns{display:flex;gap:10px;flex-wrap:wrap}

/* buttons */
.mk7-btn{border:1px solid #d1d5db;background:#fff;color:var(--ink);padding:8px 18px;border-radius:8px;font-weight:700;font-size:.88rem}
.mk7-btn:hover:not(:disabled){border-color:#9ca3af}
.mk7-btn.primary{background:var(--green);border-color:var(--green);color:#fff;box-shadow:0 2px 8px rgba(22,163,74,.25)}
.mk7-btn.primary:hover:not(:disabled){background:#15803d;border-color:#15803d}
.mk7-btn.full{width:100%}
.mk7-btn:disabled{opacity:.45;cursor:not-allowed}

/* palette panel */
.mk7-side{flex:0 0 300px;width:300px;height:100%;display:flex;flex-direction:column;background:#fff;border-left:1px solid var(--line)}
.mk7-side-head{padding:16px 18px 12px;font-size:1.05rem;font-weight:700;color:var(--green-dark);border-bottom:1px solid #eef2f0}
.mk7-legend{display:grid;grid-template-columns:1fr 1fr;gap:8px 10px;padding:12px 18px;font-size:.76rem;color:#4b5563;border-bottom:1px solid #eef2f0}
.mk7-sq{display:inline-block;width:13px;height:13px;border-radius:3px;border:1px solid #d1d5db;background:#fff;margin-right:6px;vertical-align:-3px}
.mk7-sq.answered{background:var(--green);border-color:var(--green)}
.mk7-sq.na{border-color:#fca5a5;background:#fff}
.mk7-sq.current{background:#f59e0b;border-color:#f59e0b}
.mk7-sq.marked{background:#8b5cf6;border-color:#8b5cf6}
.mk7-sq.wrongp{background:#dc2626;border-color:#dc2626}
.mk7-side-scroll{flex:1;overflow-y:auto;padding:4px 18px 14px}
.mk7-ptitle{font-size:.84rem;font-weight:700;color:var(--green-dark);margin:14px 0 10px}
.mk7-pgrid{display:grid;grid-template-columns:repeat(6,1fr);gap:6px}
.mk7-pbtn{height:36px;border-radius:7px;border:1px solid #e0e4e8;background:#fff;font-size:.78rem;font-weight:700;color:#374151;position:relative;padding:0}
.mk7-pbtn:hover{border-color:var(--green)}
.mk7-pbtn.answered{background:var(--green);border-color:var(--green);color:#fff}
.mk7-pbtn.na{border-color:#fca5a5;background:#fff5f5}
.mk7-pbtn.current{background:#f59e0b;border-color:#f59e0b;color:#fff;box-shadow:0 2px 8px rgba(245,158,11,.4)}
.mk7-pbtn.marked{background:#8b5cf6;border-color:#8b5cf6;color:#fff}
.mk7-pbtn.marked-ans{background:var(--green);border-color:var(--green);color:#fff}
.mk7-pbtn.marked-ans::after{content:"";position:absolute;top:-4px;right:-4px;width:12px;height:12px;border-radius:50%;background:#8b5cf6;border:2px solid #fff}
.mk7-pbtn.wrongp{background:#dc2626;border-color:#dc2626;color:#fff}
.mk7-pbtn.ring{outline:2.5px solid #f59e0b;outline-offset:1px}
.mk7-side-foot{padding:12px 18px 16px;border-top:1px solid #eef2f0}

/* tables */
.mk7-table-wrap{overflow:auto;margin:6px 0 12px}
.mk7-table{border-collapse:collapse;width:100%;font-size:.85rem;background:#fff}
.mk7-table th,.mk7-table td{border:1px solid #e5e7eb;padding:7px 11px;text-align:left}
.mk7-table th{background:#f3f4f6}

/* modal */
.mk7-modal-bg{position:fixed;inset:0;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;z-index:2000}
.mk7-modal{background:#fff;border-radius:14px;padding:26px;max-width:390px;width:90%}
.mk7-modal h3{margin:0 0 8px;color:var(--green-dark)}
.mk7-actions{display:flex;gap:10px;margin-top:18px;flex-wrap:wrap}

/* result */
.mk7-result-page{background:#f3faf6;padding:0 16px;overflow-y:auto;display:block}
.mk7-result{max-width:760px;margin:24px auto;background:#fff;border:1px solid #e6f4ec;border-radius:18px;padding:34px 36px;box-shadow:0 4px 24px rgba(22,101,52,.08)}
.mk7-score{font-size:3rem;font-weight:800;color:var(--green);margin-top:8px}
.mk7-score span{font-size:1.15rem;color:var(--muted);font-weight:500}
.mk7-stats{display:flex;gap:28px;margin-top:6px}
.mk7-stats div{display:flex;flex-direction:column;font-size:.82rem;color:var(--muted)}
.mk7-stats b{font-size:1.5rem;color:var(--ink)}

/* responsive */
@media(max-width:1280px){
  .mk7-split{grid-template-columns:1fr}
  .mk7-passage{position:static;max-height:320px}
}
@media(max-width:980px){
  .mk7-page{overflow-y:auto;display:block}
  .mk7-shell,.mk7-main{display:block}
  .mk7-scroll{overflow:visible}
  .mk7-headbar{position:sticky;top:0;z-index:3}
  .mk7-side{width:auto;height:auto;border-left:0;border-top:1px solid var(--line)}
  .mk7-side-scroll{overflow:visible}
  .mk7-inner{padding:14px 12px 30px}
}
@media(max-width:560px){
  .mk7-title{font-size:1.5rem}
  .mk7-qcard{padding:16px}
  .mk7-opts,.mk7-clear,.mk7-expl{margin-left:0}
}
@media(prefers-reduced-motion:reduce){.mk7-page *{transition:none !important}}
`;